import { supabase } from './supabase.js'

// Resolve the active restaurant id for the current session — FAIL-SAFE.
//
// Multi-unit context exposes currentRestaurantId, but it is intentionally
// NULL in TWO cases: a legacy single-unit account (one membership, no
// group) AND a multi-unit operator viewing the all-units roll-up (no unit
// selected). Receive forms need a CONCRETE restaurant id so the row and
// photo land on the RIGHT unit.
//
// The old behavior fell back to "first active restaurant" whenever
// currentRestaurantId was null. For a MULTI-UNIT operator that is a guess:
// it silently mis-files the delivery (and its photo) onto an arbitrary
// unit. That is exactly the receive-attribution bug. So this resolver now
// refuses to guess for grouped operators:
//
//   - currentRestaurantId present  → return it.
//   - else if isGrouped            → return null. The caller MUST block and
//                                     prompt the operator to pick a unit;
//                                     it must NOT upload or insert.
//   - else (single-unit)           → keep the first-active-via-RLS fallback,
//                                     which is unambiguous (one unit only).
//
// Returns the id string, or null. For single-unit, null means truly none
// could be resolved. For grouped, null means "no unit selected" — same
// return shape, but the caller distinguishes via isGrouped and shows the
// "select a unit" prompt rather than uploading an unauthorizable object.
export async function resolveActiveRestaurantId({ currentRestaurantId, isGrouped } = {}) {
  if (currentRestaurantId) return currentRestaurantId
  // Multi-unit operator with no unit selected (roll-up / all-units view):
  // do NOT guess. The caller blocks and prompts.
  if (isGrouped) return null
  try {
    const { data, error } = await supabase
      .from('restaurants')
      .select('id')
      .eq('active', true)
      .order('created_at')
      .limit(1)
      .single()
    if (error) return null
    return data?.id ?? null
  } catch (_e) {
    return null
  }
}
